import {
    getToken,
    setToken,
    getRefreshToken,
    setRefreshToken,
    getLastRefreshTime,
    setLastRefreshTime,
    shouldRefreshToken,
    removeToken,
    removeRefreshToken,
    removeLastRefreshTime,
} from "./auth";
import { refreshToken as refreshTokenApi, validateToken } from "../api/session";
import store from "../store";
import { getDomainSite } from "./site";
import { log2 } from "./log2";

const log = log2("tokenRefresh");

/**
 * Check if siteId is available
 * @returns {boolean}
 */
function hasSiteId() {
    // Check from store
    if (store.state.site && store.state.site.site && store.state.site.site._id) {
        return true;
    }
    // Check from localStorage
    const domainSite = getDomainSite();
    if (domainSite) {
        try {
            const site = JSON.parse(domainSite);
            if (site && site._id) {
                return true;
            }
        } catch (e) {
            // ignore
        }
    }
    return false;
}

/** Site panel oturumu (tablet session) — admin kullanıcıda yok, validate atlanır */
function isAdminSession() {
    return !!(store.state.auth && store.state.auth.adminUser != null);
}

/**
 * Token Refresh Manager
 * Handles automatic token refresh based on activity
 */
class TokenRefreshManager {
    constructor() {
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes in ms
    }

    /**
     * Check if enough time has passed since last refresh
     * @returns {boolean}
     */
    shouldRefresh() {
        const token = getToken();
        const refreshTokenValue = getRefreshToken();

        if (!token || !refreshTokenValue) {
            return false;
        }

        const lastRefresh = getLastRefreshTime();
        if (!lastRefresh) {
            return true; // Never refreshed, should refresh
        }

        const timeSinceLastRefresh = Date.now() - lastRefresh;
        return timeSinceLastRefresh >= this.REFRESH_INTERVAL;
    }

    /**
     * Refresh token if needed (called on API requests)
     * @returns {Promise<boolean>} True if refresh was successful or not needed
     */
    async refreshIfNeeded() {
        // Don't refresh if siteId is not available
        if (!hasSiteId()) {
            return true;
        }

        if (!this.shouldRefresh()) {
            return true;
        }

        return this.doRefresh();
    }

    /**
     * Perform token refresh
     * @returns {Promise<boolean>} True if refresh was successful
     */
    async doRefresh() {
        // Don't refresh if siteId is not available
        if (!hasSiteId()) {
            console.log("[TokenRefreshManager] Skipping refresh - siteId not available");
            return true;
        }

        // Prevent concurrent refresh requests
        if (this.isRefreshing) {
            return this.refreshPromise;
        }

        const refreshTokenValue = getRefreshToken();
        if (!refreshTokenValue) {
            return false;
        }

        this.isRefreshing = true;
        this.refreshPromise = this._performRefresh(refreshTokenValue);

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    /**
     * Internal refresh implementation
     * @private
     */
    async _performRefresh(refreshTokenValue) {
        try {
            const response = await refreshTokenApi(refreshTokenValue);

            if (response.data && response.data.success) {
                // Update tokens in localStorage
                setToken(response.data.token);
                setRefreshToken(response.data.refreshToken);
                setLastRefreshTime(Date.now());

                // Also update Vuex store
                store.commit("auth/SET_TOKEN", response.data.token);

                console.log("[TokenRefreshManager] Token refreshed successfully");
                return true;
            }

            return false;
        } catch (error) {
            console.error("[TokenRefreshManager] Token refresh failed:", error.message);

            // If refresh fails with 401, clear tokens
            if (error.response && error.response.status === 401) {
                this.clearTokens();
            }

            return false;
        }
    }

    /**
     * Validate current token
     * @returns {Promise<Object>} Validation result
     */
    async validateCurrentToken() {
        // Don't validate if siteId is not available
        if (!hasSiteId()) {
            log.debug("[validateCurrentToken] site-id yok, atlandı");
            return { outcome: "skipped", data: { reason: "site_not_loaded" } };
        }

        try {
            const response = await validateToken();
            const data = response.data || {};
            if (data.valid === true) {
                return { outcome: "valid", data: data };
            }
            if (data.valid === false) {
                return { outcome: "invalid", data: data };
            }
            return {
                outcome: "unknown",
                data: { reason: "unexpected_validate_body", bodyKeys: Object.keys(data) },
            };
        } catch (error) {
            const status = error.response && error.response.status;
            const url = (error.config && (error.config.url || error.config._url)) || "";
            if (status === 401) {
                return { outcome: "invalid", data: { reason: "http_401", url: url } };
            }
            if (!error.response) {
                return {
                    outcome: "unknown",
                    data: {
                        reason: "network_or_no_response",
                        message: error.message,
                        code: error.code,
                    },
                };
            }
            if (status >= 500) {
                return { outcome: "unknown", data: { reason: "http_5xx", status: status, url: url } };
            }
            return {
                outcome: "unknown",
                data: { reason: "http_" + status, url: url, message: error.message },
            };
        }
    }

    /**
     * GET /sessions/validate — token + session geçerli mi (tek istek, 200 + { valid }).
     * Ağ/DNS hatasında veya 5xx'te oturumu düşürmez (outcome unknown → true).
     * site-id yoksa doğrulama yapılmaz, true döner.
     * @param {string} [context] - log için: init, isLoggedIn, fetchStartupDatas…
     * @returns {Promise<boolean>}
     */
    async ensureSessionValid(context) {
        var ctx = context || "ensureSessionValid";
        const token = getToken();
        if (!token) {
            log.debug("[ensureSessionValid] token yok", { context: ctx });
            return false;
        }
        if (isAdminSession()) {
            log.debug("[ensureSessionValid] admin kullanıcı, /sessions/validate atlanıyor", { context: ctx });
            return true;
        }
        if (!hasSiteId()) {
            return true;
        }
        const vr = await this.validateCurrentToken();
        if (vr.outcome === "valid" || vr.outcome === "skipped") {
            return true;
        }
        if (vr.outcome === "invalid") {
            log.warn("[ensureSessionValid] oturum geçersiz — logout akışı tetiklenebilir", {
                context: ctx,
                reason: vr.data && vr.data.reason,
                data: vr.data,
            });
            return false;
        }
        log.warn("[ensureSessionValid] doğrulama sonuçsuz (ağ/5xx vb.) — logout yapılmayacak", {
            context: ctx,
            data: vr.data,
        });
        return true;
    }

    /**
     * Initialize on app startup
     * Checks if token exists and tries to refresh if needed
     * @returns {Promise<boolean>} True if token is valid
     */
    async initializeOnStartup() {
        const token = getToken();
        const refreshTokenValue = getRefreshToken();

        if (!token) {
            return false;
        }

        if (isAdminSession()) {
            log.debug("[initializeOnStartup] admin kullanıcı, session doğrulama ve tablet refresh atlanıyor");
            return true;
        }

        // Skip if siteId is not available yet
        if (!hasSiteId()) {
            log.debug("[initializeOnStartup] site-id yok, validate ertelendi");
            return true; // Assume valid, refresh will happen in request interceptor
        }

        // If we have a refresh token and enough time has passed, try to refresh
        if (refreshTokenValue && this.shouldRefresh()) {
            log.info("[initializeOnStartup] zamanı gelmiş refresh deneniyor");
            await this.doRefresh();
            if (!getToken()) {
                log.warn("[initializeOnStartup] refresh sonrası token yok");
                return false;
            }
        }

        var ok = await this.ensureSessionValid("initializeOnStartup");
        if (!ok) {
            log.warn("[initializeOnStartup] ensureSessionValid false");
        }
        return ok;
    }

    /**
     * Clear all tokens
     */
    clearTokens() {
        removeToken();
        removeRefreshToken();
        removeLastRefreshTime();
    }
}

// Export singleton instance
export const tokenRefreshManager = new TokenRefreshManager();

// Export for use in request interceptors
export async function refreshTokenIfNeeded() {
    return tokenRefreshManager.refreshIfNeeded();
}

export async function ensureSessionValid(context) {
    return tokenRefreshManager.ensureSessionValid(context);
}
